import { motion } from 'framer-motion'
import { ArrowRightIcon, PlayIcon } from '@heroicons/react/24/outline'
import { Link } from 'react-router-dom'
import { useState } from 'react'
import { 
  AIIcon, 
  WebDevIcon, 
  AutomationIcon, 
  CloudIcon, 
  SecurityIcon, 
  TransformIcon 
} from '@/components/icons/ServiceIcons'

interface Service {
  id: string
  title: string
  description: string
  shortDesc: string
  icon: React.ComponentType<{ className?: string; animate?: boolean }>
  featured: boolean
  category: string
  link: string
}

const services: Service[] = [
  {
    id: 'ai',
    title: 'Inteligencia Artificial',
    description: 'Desarrollo de agentes IA, machine learning y automatización inteligente para revolucionar tus procesos empresariales.',
    shortDesc: 'Agentes IA y automatización inteligente para procesos empresariales avanzados',
    icon: AIIcon,
    featured: true,
    category: 'AI & Machine Learning',
    link: '/servicios/inteligencia-artificial'
  },
  {
    id: 'web-dev',
    title: 'Desarrollo Web',
    description: 'Aplicaciones web modernas, responsivas y escalables con las últimas tecnologías y mejores prácticas.',
    shortDesc: 'Aplicaciones web modernas y escalables con tecnologías de vanguardia',
    icon: WebDevIcon,
    featured: false,
    category: 'Desarrollo',
    link: '/servicios/desarrollo-web'
  },
  {
    id: 'automation',
    title: 'Automatización',
    description: 'Automatización de procesos empresariales, workflows inteligentes y optimización de operaciones.',
    shortDesc: 'Automatización de procesos y workflows para optimizar operaciones',
    icon: AutomationIcon,
    featured: false,
    category: 'Automatización',
    link: '/servicios/automatizacion'
  },
  {
    id: 'cloud',
    title: 'Soluciones Cloud',
    description: 'Migración a la nube, arquitecturas serverless y infraestructura escalable para tu negocio.',
    shortDesc: 'Migración a la nube e infraestructura escalable para empresas',
    icon: CloudIcon,
    featured: false,
    category: 'Infraestructura',
    link: '/servicios/cloud'
  },
  {
    id: 'security',
    title: 'Ciberseguridad',
    description: 'Auditorías de seguridad, implementación de protocolos y protección avanzada de datos.',
    shortDesc: 'Auditorías de seguridad y protección avanzada de datos empresariales',
    icon: SecurityIcon,
    featured: false,
    category: 'Seguridad',
    link: '/servicios/ciberseguridad'
  }
]

export default function Servicios() {
  const [hoveredCard, setHoveredCard] = useState<string | null>(null)

  const handleScrollToContact = () => {
    const contactSection = document.getElementById('contacto')
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' })
    } else {
      // Si no existe la sección, navegar a la página de contacto
      window.location.href = '/contacto'
    }
  }

  return (
    <div className="pt-32 pb-20">
      {/* Header Section */}
      <div className="services-header">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="services-title">
            Nuestros Servicios
          </h1>
          <p className="services-subtitle">
            Soluciones tecnológicas avanzadas diseñadas para impulsar tu negocio hacia el futuro digital con innovación y excelencia.
          </p>
        </motion.div>
      </div>

      {/* Services Grid */}
      <section className="max-w-7xl mx-auto px-6 mb-20">
        <div className="service-card-grid">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              onHoverStart={() => setHoveredCard(service.id)}
              onHoverEnd={() => setHoveredCard(null)}
              className={service.featured ? 'service-card-featured-container' : ''}
            >
              <Link to={service.link}>
                <div className={`service-card ${service.featured ? 'service-card-featured' : ''}`}>
                  <div className="service-card-content">
                    {/* Category Tag */}
                    <div className="flex items-center justify-between">
                      <span className="service-card-category">
                        {service.category}
                      </span>
                      {service.featured && (
                        <span style={{
                          background: 'rgba(15, 246, 252, 0.2)',
                          border: '1px solid rgba(15, 246, 252, 0.5)',
                          color: '#0ff6fc',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '9999px'
                        }}>
                          DESTACADO
                        </span>
                      )}
                    </div>

                    {/* Icon */}
                    <div className="service-card-icon">
                      <motion.div
                        animate={hoveredCard === service.id ? { scale: 1.05 } : { scale: 1 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                      >
                        <service.icon 
                          className={service.featured ? "w-16 h-16" : "w-12 h-12"} 
                          animate={hoveredCard === service.id}
                        />
                      </motion.div>
                    </div>

                    {/* Title */}
                    <h3 className={`service-card-title ${service.featured ? 'service-card-title-featured' : ''}`}>
                      {service.title}
                    </h3>

                    {/* Description */}
                    <p className="service-card-description">
                      {service.featured ? service.description : service.shortDesc}
                    </p>

                    {/* CTA Button */}
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="service-card-button">
                        <span>Ver detalles</span>
                        <ArrowRightIcon 
                          className="h-4 w-4" 
                          style={{ 
                            transform: hoveredCard === service.id ? 'translateX(4px)' : 'translateX(0)',
                            transition: 'transform 0.3s ease'
                          }} 
                        />
                      </div>
                    </motion.div>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section - Redesigned */}
      <section className="cta-transform-section">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="cta-transform-card"
        >
          {/* Background with Gradient and Glassmorphism */}
          <div className="cta-transform-bg" />
          <div className="cta-transform-overlay" />
          
          {/* Tech Grid Overlay */}
          <div className="absolute inset-0 tech-grid opacity-20" />
          
          {/* Content */}
          <div className="cta-transform-content">
            {/* Transform Icon */}
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              whileInView={{ scale: 1, rotate: 0 }}
              transition={{ duration: 1, ease: "easeOut" }}
              viewport={{ once: true }}
              className="cta-transform-icon"
            >
              <TransformIcon className="w-20 h-20" animate={true} />
            </motion.div>

            {/* Title */}
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className="cta-transform-title"
            >
              ¿Listo para Transformar tu Negocio?
            </motion.h2>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
              className="cta-transform-subtitle"
            >
              Únete a las empresas que ya han dado el salto hacia la innovación digital y el futuro tecnológico
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
              className="cta-transform-buttons"
            >
              {/* Primary CTA */}
              <motion.button
                onClick={handleScrollToContact}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="cta-primary-button"
              >
                <span>Comenzar Ahora</span>
                <ArrowRightIcon className="h-6 w-6" />
              </motion.button>

              {/* Secondary CTA */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="cta-secondary-button"
              >
                <PlayIcon className="h-6 w-6" />
                <span>Ver Demo</span>
              </motion.button>
            </motion.div>

            {/* Trust Indicators */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              viewport={{ once: true }}
              className="cta-trust-indicators"
            >
              <div className="cta-trust-item">
                <div className="cta-trust-dot cta-trust-dot-1" />
                <span>Consulta gratuita</span>
              </div>
              <div className="cta-trust-item">
                <div className="cta-trust-dot cta-trust-dot-2" />
                <span>Sin compromiso</span>
              </div>
              <div className="cta-trust-item">
                <div className="cta-trust-dot cta-trust-dot-3" />
                <span>Respuesta en 24h</span>
              </div>
            </motion.div>
          </div>

          {/* Floating Elements */}
          <motion.div
            animate={{ y: [-10, 10, -10], x: [-5, 5, -5] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="cta-floating-elements cta-floating-dot-1"
          />
          <motion.div
            animate={{ y: [10, -10, 10], x: [5, -5, 5] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            className="cta-floating-elements cta-floating-dot-2"
          />
          <motion.div
            animate={{ y: [-5, 15, -5], x: [-3, 3, -3] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            className="cta-floating-elements cta-floating-dot-3"
          />
        </motion.div>
      </section>
    </div>
  )
}
